import { httpClient } from "./client";
import type { CoachApi, CoachListParams, LessonRequestApi } from "./interfaces";
import type {
  ClubCoachProfilePublicResponse,
  CoachLessonServiceResponse,
  LessonRequestResponse,
} from "../types/generated";

/**
 * coachApi — the PLAYER-callable READ surface of the coach module (router
 * /coaches, ADR-006/006b). All endpoints sit behind get_current_user. httpClient
 * prefixes every path with `/bff/api`, so the BFF sees `/bff/api/coaches...`.
 *
 *   listCoaches(params)          → GET /coaches?facility_id&status&limit&offset
 *                                    → {items: CoachProfileResponse[], total}
 *   getCoach(id)                 → GET /coaches/{id}            → CoachProfileResponse
 *   getCoachServices(id)         → GET /coaches/{id}/services   → CoachLessonServiceResponse[]
 *   getCoachAffiliations(id)     → GET /coaches/{id}/affiliations → CoachAffiliationResponse[]
 *
 * Thin client: service Decimal prices are server-computed. The frontend never
 * performs raw arithmetic on a Decimal field.
 */
export const coachApi: CoachApi = {
  listCoaches: (params: CoachListParams) => {
    const sp = new URLSearchParams();
    if (params.facility_id) sp.set("facility_id", params.facility_id);
    if (params.limit !== undefined) sp.set("limit", String(params.limit));
    if (params.offset !== undefined) sp.set("offset", String(params.offset));
    const qs = sp.toString();
    return httpClient.get<ClubCoachProfilePublicResponse[]>(
      qs
        ? `/coaches/club-profiles/public?${qs}`
        : "/coaches/club-profiles/public",
    );
  },

  getCoach: (coachProfileId: string, facilityId?: string) =>
    httpClient.get<ClubCoachProfilePublicResponse>(
      `/coaches/club-profiles/${coachProfileId}/public${facilityId ? `?facility_id=${encodeURIComponent(facilityId)}` : ""}`,
    ),

  getCoachServices: (coachProfileId: string, facilityId: string) =>
    httpClient.get<CoachLessonServiceResponse[]>(
      `/coaches/club-profiles/${coachProfileId}/services?facility_id=${encodeURIComponent(facilityId)}`,
    ),

  createProposalTemplate: (coachProfileId, body) =>
    httpClient.post(
      `/coaches/club-profiles/${coachProfileId}/proposal-templates`, body,
    ),

  listProposalTemplates: (coachProfileId, facilityId) =>
    httpClient.get(
      `/coaches/club-profiles/${coachProfileId}/proposal-templates?facility_id=${encodeURIComponent(facilityId)}`,
    ),

  updateProposalTemplate: (templateId, body) =>
    httpClient.put(`/coaches/proposal-templates/${templateId}`, body),

  archiveProposalTemplate: (templateId) =>
    httpClient.delete(`/coaches/proposal-templates/${templateId}`),
};

/**
 * Retained caller-scoped request history. New contact uses inquiryApi; staff
 * converts admitted inquiries through coachingApi before preparing an offer.
 *
 *   listMine(params?)          → GET /lesson-requests?status= → {items, total}
 *
 * listMine is CALLER-SCOPED server-side — a player gets their OWN requests (as
 * requester). NO uid is ever in the query (no IDOR surface); only an optional
 * `status` filter. Versioned proposal commands and accepted funding use coachingApi.
 */
export const lessonRequestApi: LessonRequestApi = {
  listMine: (params: { status?: string } = {}) => {
    const sp = new URLSearchParams();
    if (params.status) sp.set("status", params.status);
    const qs = sp.toString();
    return httpClient.get<{ items: LessonRequestResponse[]; total: number }>(
      qs ? `/lesson-requests?${qs}` : "/lesson-requests",
    );
  },
};
